import type {IBudgetLineInput, ISpendCategory} from './walletCashflowTypes';

export type TWalletCashflowCostSource =
  | 'production.seed'
  | 'production.fertiliser'
  | 'production.chemical'
  | 'operating'
  | 'fundingAsk'
  | 'unknown';

export interface IWalletCashflowCostInput {
  label: string;
  cost: number;
  source?: TWalletCashflowCostSource;
}

export interface IWalletCashflowUnmappedCost {
  label: string;
  amount: number;
  source: TWalletCashflowCostSource;
}

export interface IWalletCashflowBudgetLineBuildResult {
  lines: IBudgetLineInput[];
  unmappedItems: IWalletCashflowUnmappedCost[];
}

export interface IWalletCashflowCatalogueDrift {
  fdExpensesMissingWalletCategory: string[];
  walletCategoriesMissingFdExpense: string[];
}

const PRODUCTION_LABEL_TO_SPEND_CATEGORY: Record<string, string> = {
  seed: 'Seed',
  fertiliser: 'Fertilizer',
  fertilizer: 'Fertilizer',
  chemical: 'Chemicals',
  chemicals: 'Chemicals',
};

const EXPECTED_WALLET_ONLY_CATEGORY_LABELS = new Set(['irrigation system']);

function lshNormaliseCategoryLabel(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function lshGetMappedLabel(label: string): string {
  const normalised = lshNormaliseCategoryLabel(label);
  return PRODUCTION_LABEL_TO_SPEND_CATEGORY[normalised] || label;
}

function lshBuildCategoryLookup(categories: ISpendCategory[]) {
  const lookup = new Map<string, ISpendCategory>();
  categories.forEach((category) => {
    if (!category?.label) return;
    lookup.set(lshNormaliseCategoryLabel(category.label), category);
    if (category.code) {
      lookup.set(lshNormaliseCategoryLabel(category.code), category);
    }
  });
  return lookup;
}

function lshBuildLabelMap(
  labels: Array<string | undefined>,
): Map<string, string> {
  const labelMap = new Map<string, string>();
  labels.forEach((label) => {
    if (!label) return;
    const key = lshNormaliseCategoryLabel(label);
    if (key && !labelMap.has(key)) {
      labelMap.set(key, label);
    }
  });
  return labelMap;
}

export function lshFindSpendCategoryForApplicationCost(
  label: string,
  categories: ISpendCategory[],
): ISpendCategory | undefined {
  const lookup = lshBuildCategoryLookup(categories);
  const mappedLabel = lshGetMappedLabel(label);
  return lookup.get(lshNormaliseCategoryLabel(mappedLabel));
}

export function lshBuildBudgetLinesFromApplicationCosts(
  costs: IWalletCashflowCostInput[],
  categories: ISpendCategory[],
): IWalletCashflowBudgetLineBuildResult {
  const lineTotals = new Map<string, number>();
  const unmappedItems: IWalletCashflowUnmappedCost[] = [];

  costs.forEach((cost) => {
    const amount = Number(cost.cost);
    if (!Number.isFinite(amount) || amount <= 0) return;

    const category = lshFindSpendCategoryForApplicationCost(
      cost.label,
      categories,
    );

    if (!category) {
      unmappedItems.push({
        label: cost.label,
        amount,
        source: cost.source || 'unknown',
      });
      return;
    }

    lineTotals.set(category.id, (lineTotals.get(category.id) || 0) + amount);
  });

  return {
    lines: Array.from(lineTotals.entries()).map(
      ([spendCategoryId, allocatedAmount]) => ({
        spendCategoryId,
        allocatedAmount,
      }),
    ),
    unmappedItems,
  };
}

export function lshAssertNoUnmappedWalletCashflowCosts(
  unmappedItems: IWalletCashflowUnmappedCost[],
): void {
  if (!unmappedItems.length) return;
  throw new Error(
    `Cannot create approved cashflow. Unmapped spend categor${
      unmappedItems.length === 1 ? 'y' : 'ies'
    }: ${unmappedItems.map((item) => item.label).join(', ')}`,
  );
}

export function lshCompareFdExpensesToSpendCategories(
  fdExpenses: Array<{name?: string}>,
  spendCategories: ISpendCategory[],
): IWalletCashflowCatalogueDrift {
  const fdLabels = lshBuildLabelMap(fdExpenses.map((expense) => expense.name));
  const walletLabels = lshBuildLabelMap(
    spendCategories.map((category) => category.label),
  );

  return {
    fdExpensesMissingWalletCategory: Array.from(fdLabels.entries())
      .filter(([label]) => !walletLabels.has(label))
      .map(([, originalLabel]) => originalLabel),
    walletCategoriesMissingFdExpense: Array.from(walletLabels.entries())
      .filter(
        ([label]) =>
          !fdLabels.has(label) &&
          !EXPECTED_WALLET_ONLY_CATEGORY_LABELS.has(label),
      )
      .map(([, originalLabel]) => originalLabel),
  };
}
